import type { FetchBaseQueryError } from '@reduxjs/toolkit/query';

import { config } from '@/config';
import { CACHE } from '@/lib/utils';
import type { RootState } from '@/store';
import { baseApi } from '@/store/baseApi';

import type {
  ApiUsage,
  CreditBurnResponse,
  CreditPlan,
  CustomerPortalResponse,
  MyCredits,
  PaymentSessionResponse,
  PaymentStatusResponse,
  StartPaymentSessionPayload,
  TransactionListResponse,
  TransactionMeta,
  TransactionQueryParams,
} from '../../types';

export const billingApi = baseApi.injectEndpoints({
  endpoints: (build) => ({
    getApiUsage: build.query<ApiUsage, void>({
      query: () => '/api/auth/v1/api-usage/',
      keepUnusedDataFor: CACHE.SHORT,
      providesTags: ['Billing'],
    }),

    getCreditBurn: build.query<CreditBurnResponse, number>({
      query: (days) => `/api/auth/v1/billing/credit-burn/?days=${days}`,
      keepUnusedDataFor: CACHE.SHORT,
      providesTags: ['Billing'],
    }),

    getMyCredits: build.query<MyCredits, void>({
      query: () => '/api/auth/v1/my-credits/',
      keepUnusedDataFor: CACHE.SHORT,
      providesTags: ['Billing'],
    }),

    getCreditPlans: build.query<CreditPlan[], void>({
      query: () => '/api/auth/v1/credit-plans/',
      keepUnusedDataFor: CACHE.LONG,
      providesTags: ['CreditPlan'],
    }),

    getTransactions: build.query<TransactionListResponse, TransactionQueryParams>({
      queryFn: async ({ page, page_size }, api) => {
        const token = (api.getState() as RootState).auth?.token;
        const headers: HeadersInit = {};
        if (token) headers['Authorization'] = `Bearer ${token}`;
        const sp = new URLSearchParams();
        sp.set('page', String(page));
        sp.set('page_size', String(page_size));
        try {
          const response = await fetch(
            `${config.apiUrl}/api/auth/v1/billing/transactions/?${sp.toString()}`,
            { headers },
          );
          const json = (await response.json()) as {
            data: TransactionListResponse['data'];
            meta: TransactionMeta;
          };
          if (!response.ok) {
            return { error: { status: response.status, data: json } as FetchBaseQueryError };
          }
          return { data: { data: json.data, meta: json.meta } };
        } catch (e) {
          return { error: { status: 'FETCH_ERROR', error: String(e) } as FetchBaseQueryError };
        }
      },
      keepUnusedDataFor: CACHE.SHORT,
      providesTags: ['Transaction'],
    }),

    startPaymentSession: build.mutation<PaymentSessionResponse, StartPaymentSessionPayload>({
      query: (body) => ({
        url: '/api/auth/v1/create-payment-session/',
        method: 'POST',
        body,
      }),
    }),

    createCustomerPortalSession: build.mutation<CustomerPortalResponse, void>({
      query: () => ({ url: '/api/auth/v1/create-customer-portal-session/', method: 'POST' }),
    }),

    checkPaymentStatus: build.query<PaymentStatusResponse, string>({
      query: (sessionId) => `/api/auth/v1/check-payment-status/?session_id=${sessionId}`,
      providesTags: ['Transaction'],
    }),
  }),
});

export const {
  useGetApiUsageQuery,
  useGetCreditBurnQuery,
  useGetMyCreditsQuery,
  useGetCreditPlansQuery,
  useGetTransactionsQuery,
  useStartPaymentSessionMutation,
  useCreateCustomerPortalSessionMutation,
  useCheckPaymentStatusQuery,
} = billingApi;
